import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Importar para la navegación
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Webcam, User, Mail, Phone, Hash, ExternalLink } from "lucide-react"; 
import { getCustomerWithCamaras } from '../api/UserEditorDashboard.api'; 
import { CustomerWithCamaras } from "../model/CustomerWithCamara"; 
import { Camara } from "../../customerDashboard/types/Camara";

const DASHBOARDURL = "https://pro.lisual.com"; 

const UserEditorDashboard: React.FC = () => {
  const navigate = useNavigate(); // Hook de navegación
  const [loading, setLoading] = useState<boolean>(false);
  const [customerWithCamaras, setCustomerWithCamaras] = useState<CustomerWithCamaras[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null); 
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "asc">("desc");
  const [sortField, setSortField] = useState<"createdAt" | "name">("createdAt");

  const fetchCustomerWithCamaras = async () => {
    setLoading(true);
    setError(null);
    try {
      const data: CustomerWithCamaras[] = await getCustomerWithCamaras();
      setCustomerWithCamaras(data);
    } catch (error) {
      setError(error as Error); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerWithCamaras();
  }, []);

  const handleShowCustomerInfo = (customerId: string) => {
    setSelectedCustomerId(selectedCustomerId === customerId ? null : customerId);
  };

  const handleAccessCamara = (camaraIdentifier: string) => {
    const token = localStorage.getItem('authToken'); 
    window.open(`${DASHBOARDURL}/dashboard/${camaraIdentifier}?AT=${token}`, '_blank');
  };

  const filteredAndSortedCustomers = customerWithCamaras
    .filter((customer) => {
      const searchLower = searchTerm.toLowerCase();
      return customer.name?.toLowerCase().includes(searchLower);
    })
    .filter((customer) => customer.visibleForEditor === true)
    .sort((a, b) => {
      if (sortField === "createdAt") {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      } else {
        const nameA = a.name?.toLowerCase() || "";
        const nameB = b.name?.toLowerCase() || "";
        return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
    });

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Acceso de cámaras por cliente</h1>
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Buscar cliente por nombre..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
        ) : (
          <div className="overflow-hidden bg-white shadow-xl rounded-xl">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider cursor-pointer" onClick={() => { setSortField("name"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); }}>
                    Cliente {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider cursor-pointer" onClick={() => { setSortField("createdAt"); setSortOrder(sortOrder === "asc" ? "desc" : "asc"); }}>
                    Fecha Alta {sortField === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Cámaras</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndSortedCustomers.map((customer) => (
                  <React.Fragment key={customer.id}>
                    <tr 
                      onClick={() => handleShowCustomerInfo(customer.id ?? "")}
                      className={`cursor-pointer transition-colors ${selectedCustomerId === customer.id ? "bg-blue-50" : "hover:bg-gray-50"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{customer.name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(customer.createdAt).toLocaleDateString('es-ES')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-bold">{customer.camaras.length}</td>
                    </tr>

                    {selectedCustomerId === customer.id && (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 bg-gray-50 border-t border-b border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Información del Cliente */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                              <div className="flex justify-between items-start mb-4">
                                <h4 className="text-lg font-bold text-blue-800">Información de Contacto</h4>
                                <button 
                                  onClick={() => navigate(`/customerDashboard/${customer.customerId}`)}
                                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition"
                                >
                                  Ir al Dashboard <ExternalLink size={14} />
                                </button>
                              </div>
                              <div className="space-y-2 text-sm">
                                <p className="flex items-center gap-2"><Hash size={16} className="text-gray-400"/> <strong>ID Cliente:</strong> {customer.customerId}</p>
                                <p className="flex items-center gap-2"><User size={16} className="text-gray-400"/> <strong>Contacto:</strong> {customer.mainUserName}</p>
                                <p className="flex items-center gap-2"><Mail size={16} className="text-gray-400"/> <strong>Email:</strong> {customer.mainUserEmail}</p>
                                <p className="flex items-center gap-2"><Phone size={16} className="text-gray-400"/> <strong>Teléfono:</strong> {customer.mainUserPhone}</p>
                              </div>
                            </div>

                            {/* Lista de Cámaras */}
                            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                              <h4 className="text-lg font-bold text-gray-800 mb-4">Cámaras Activas</h4>
                              <div className="max-h-40 overflow-y-auto space-y-2 pr-2">
                                {customer.camaras.map((camara, idx) => (
                                  <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded border border-gray-100">
                                    <span className="text-sm font-medium text-gray-700">{camara.Name}</span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAccessCamara(camara.identifier); }}
                                      className="p-1.5 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                                    >
                                      <Webcam size={18} />
                                    </button>
                                  </div>
                                ))}
                                {customer.camaras.length === 0 && <p className="text-gray-400 text-sm">Sin cámaras asociadas.</p>}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ToastContainer />
      </div>
    </div>
  );
}

export default UserEditorDashboard;